import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { TeamDropdown } from '../../components/common/TeamDropdown';
import { fetchPlayerTeams, loadMorePlayerTeams, fetchTeamPlayers, setSelectedTeam, clearTeamError } from '../../features/team/teamSlice';

export const LoginPlayerPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { teams, teamPlayers, error, playerTeamsPagination, playerTeamsLoading } = useSelector((state) => state.team);

  const [formData, setFormData] = useState({
    team: null,
    player: null
  });
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const searchTermRef = useRef('');

  useEffect(() => {
    // Clear error when component mounts
    dispatch(clearTeamError());
    dispatch(fetchPlayerTeams({ page: 1, limit: 20 }));

    // Clear error when component unmounts
    return () => {
      dispatch(clearTeamError());
    };
  }, [dispatch]);

  // Handle search - called from TeamDropdown with debounce
  const handleSearch = useCallback((term) => {
    searchTermRef.current = term;
    dispatch(fetchPlayerTeams({ page: 1, limit: 20, teamName: term || undefined }));
  }, [dispatch]);

  // Handle load more - called when user scrolls to bottom
  const handleLoadMore = useCallback(() => {
    if (loadingMore || !playerTeamsPagination?.hasMore) return;

    setLoadingMore(true);
    const nextPage = playerTeamsPagination.currentPage + 1;
    dispatch(loadMorePlayerTeams({
      page: nextPage,
      limit: 20,
      teamName: searchTermRef.current || undefined
    })).finally(() => {
      setLoadingMore(false);
    });
  }, [dispatch, loadingMore, playerTeamsPagination]);

  const handleTeamSelect = (team) => {
    const teamId = team.id || team._id;
    setFormData({ team, player: null });
    setLoadingPlayers(true);
    dispatch(setSelectedTeam(teamId));
    dispatch(fetchTeamPlayers(teamId)).finally(() => {
      setLoadingPlayers(false);
    });
  };

  const handlePlayerSelect = (player) => {
    setFormData({ ...formData, player });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.team || !formData.player) return;

    const teamId = formData.team.id || formData.team._id;
    const playerName = formData.player.name || formData.player.fullName || '';

    navigate('/auth/player-plans', {
      state: {
        teamId,
        playerName,
      }
    });
  };

  return (
    <main className="min-h-screen bg-jp-page text-jp-ink text-left overflow-x-hidden">
      {/* Navy strip behind the see-through navbar so its white logo and links stay readable */}
      <div className="bg-jp-band h-16 sm:h-20" aria-hidden="true" />

      <section className="px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-16 sm:pb-20">
        <div className="max-w-2xl mx-auto text-center mb-8">
          <p className="text-jp-accent dark:text-jp-gold text-sm font-bold uppercase tracking-widest mb-3">
            For Players
          </p>
          <h1 className="text-5xl sm:text-6xl font-anton leading-[1.1] tracking-tight text-jp-ink">
            CLAIM YOUR PLAYER PROFILE
          </h1>
          <p className="mt-4 text-jp-muted text-sm sm:text-base">
            Pick your team, then find your name.
          </p>
        </div>

        <div className="max-w-2xl mx-auto bg-jp-card rounded-2xl border border-jp-outline/70 shadow-sm p-5 sm:p-8">
          <form onSubmit={handleSubmit} className="rounded-xl p-5 sm:p-6 bg-jp-section border border-jp-outline/50 space-y-5">
            {error && (
              <div className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/40 text-red-700 dark:text-red-300 text-sm">
                {error}
              </div>
            )}

            {/* Team Dropdown */}
            <div>
              <span className="block text-sm font-semibold text-jp-ink mb-1.5">
                Which team do you play for?
              </span>
              <TeamDropdown
                selectedTeam={formData.team}
                onSelectTeam={handleTeamSelect}
                teams={teams || []}
                loading={playerTeamsLoading && teams.length === 0}
                loadingText="Loading teams..."
                placeholder="Select team"
                variant="themed"
                onSearch={handleSearch}
                onLoadMore={handleLoadMore}
                hasMore={playerTeamsPagination?.hasMore || false}
                loadingMore={loadingMore}
              />
            </div>

            {/* Player Dropdown */}
            <div>
              <span className="block text-sm font-semibold text-jp-ink mb-1.5">
                Find your name in the list of players
              </span>
              <TeamDropdown
                selectedTeam={formData.player}
                onSelectTeam={handlePlayerSelect}
                teams={teamPlayers || []}
                loading={loadingPlayers}
                loadingText="Loading players..."
                placeholder="Select name"
                variant="themed"
                searchPlaceholder="Search players..."
              />
            </div>

            <div className="max-w-md mx-auto pt-3">
              <button
                type="submit"
                className="w-full py-4 text-base font-bold uppercase tracking-wider bg-gradient-to-r from-cyan-400 via-cyan-500 to-blue-600 text-slate-900 rounded-xl hover:brightness-110 transition-all shadow-lg shadow-cyan-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent focus-visible:ring-offset-2 focus-visible:ring-offset-jp-section active:scale-[0.98]"
              >
                Next
              </button>
            </div>
          </form>

          <p className="px-2 pt-5 pb-1 text-center text-sm sm:text-base text-jp-muted">
            Didn't find your team?{' '}
            <Link to="/auth/addition-request" className="font-semibold text-jp-accent hover:underline">
              Request addition
            </Link>
          </p>
        </div>
      </section>
    </main>
  );
};

export default LoginPlayerPage;
